import { Point as FabricPoint } from 'fabric';

import { Dimensions, SpreadGroundImage } from 'editor/src/store/design/types';

import { getSpreadGroundScales } from 'editor/src/util/getSpreadGroundImageScale';

export function getSpreadGroundOffsets(
  image: SpreadGroundImage,
  contentDimensions: Dimensions,
  // Defaults to the legacy per-axis math: several callers frame a grouped
  // spread, where contentDimensions covers the whole group and the uniform
  // scale would not describe how the ground was drawn.
  allowUniformScale = false,
) {
  // The label rect's position in canvas space has to use the same scales the
  // bg/fg image is drawn with (see getSpreadGroundImageScale) — otherwise the
  // pan computed here points at a label location that doesn't match where the
  // image actually ended up.
  const { scaleX, scaleY } = getSpreadGroundScales(image, contentDimensions, allowUniformScale);
  return {
    offsetLeft: Math.abs(image.left) * scaleX,
    offsetTop: Math.abs(image.top) * scaleY,
  };
}

export function getSpreadImageZoom(imageDim: Dimensions, canvasDimensions: Dimensions) {
  return imageDim.width > imageDim.height
    ? canvasDimensions.width / imageDim.width
    : canvasDimensions.height / imageDim.height;
}

function getSpreadImageCoverZoom(imageDim: Dimensions, canvasDimensions: Dimensions) {
  return Math.max(canvasDimensions.width / imageDim.width, canvasDimensions.height / imageDim.height);
}

function getMinZoom(
  background: Dimensions | undefined,
  foreground: Dimensions | undefined,
  canvasDimensions: Dimensions,
  defaultZoom = 1,
) {
  const backgroundZoom = background ? getSpreadImageZoom(background, canvasDimensions) : defaultZoom;
  const foregroundZoom = foreground ? getSpreadImageZoom(foreground, canvasDimensions) : defaultZoom;
  return Math.min(backgroundZoom, foregroundZoom);
}

function getCoverZoom(
  background: Dimensions | undefined,
  foreground: Dimensions | undefined,
  canvasDimensions: Dimensions,
  defaultZoom = 1,
) {
  // Skip missing layers entirely — feeding them a defaultZoom of 1 would
  // dominate the Math.max when the present image needs to be scaled down
  // (bgZoom < 1), leaving the viewport at 1:1 and showing only a slice.
  const zooms: number[] = [];
  if (background) {
    zooms.push(getSpreadImageCoverZoom(background, canvasDimensions));
  }
  if (foreground) {
    zooms.push(getSpreadImageCoverZoom(foreground, canvasDimensions));
  }
  return zooms.length ? Math.max(...zooms) : defaultZoom;
}

function getThumbnailZoomAndPan(
  defaultZoom: 'spread' | 'auto' | 'cover' | number,
  noCentering: boolean,
  canvasDim: Dimensions,
  contentDimensions: Dimensions,
  background: {
    image: SpreadGroundImage | undefined;
    dimensions: Dimensions | undefined;
  },
  foreground: {
    image: SpreadGroundImage | undefined;
    dimensions: Dimensions | undefined;
  },
  // Fraction of the viewport to keep free around the content, 0–1. Shaped
  // spreads need it: their bg/fg asset is drawn edge to edge, so at a fitted
  // zoom the outline sits exactly on the viewport border and reads as cut off.
  contentPadding = 0,
  // False for a grouped spread: contentDimensions then spans several spreads
  // while the ground's rect describes one, so their aspects legitimately differ
  // and the grounds are stretched across the group on purpose.
  allowUniformGroundScale = false,
): { zoom: number; center: FabricPoint; maxWidth: number; maxHeight: number } {
  if (defaultZoom === 'spread' || (defaultZoom !== 'auto' && defaultZoom !== 'cover')) {
    const zoom =
      defaultZoom === 'spread' ? getMinZoom(background.dimensions, foreground.dimensions, canvasDim) : defaultZoom;

    const diffX = (canvasDim.width - contentDimensions.width * zoom) / 2;
    const diffY = (canvasDim.height - contentDimensions.height * zoom) / 2;

    return {
      zoom,
      center: noCentering ? new FabricPoint(0, 0) : new FabricPoint(-diffX, -diffY),
      maxWidth: canvasDim.width,
      maxHeight: canvasDim.height,
    };
  }

  const fittedZoom =
    defaultZoom === 'cover'
      ? getCoverZoom(background.dimensions, foreground.dimensions, canvasDim)
      : getMinZoom(background.dimensions, foreground.dimensions, canvasDim);
  const zoom = fittedZoom * (1 - contentPadding);
  const backgroundOffset =
    (background.image && getSpreadGroundOffsets(background.image, contentDimensions, allowUniformGroundScale)) || null;
  const foregroundOffset =
    (foreground.image && getSpreadGroundOffsets(foreground.image, contentDimensions, allowUniformGroundScale)) || null;

  const maxWidth =
    Math.max(background.dimensions?.width || 0, foreground.dimensions?.width || 0, contentDimensions.width) * zoom;
  const offsetLeft = Math.max(backgroundOffset?.offsetLeft || 0, foregroundOffset?.offsetLeft || 0, 0) * zoom;
  const maxHeight =
    Math.max(background.dimensions?.height || 0, foreground.dimensions?.height || 0, contentDimensions.height) * zoom;
  const offsetTop = Math.max(backgroundOffset?.offsetTop || 0, foregroundOffset?.offsetTop || 0, 0) * zoom;

  return {
    zoom,
    center: new FabricPoint(
      -offsetLeft + (maxWidth - canvasDim.width) / 2,
      -offsetTop + (maxHeight - canvasDim.height) / 2,
    ),
    maxWidth,
    maxHeight,
  };
}

export default getThumbnailZoomAndPan;
